import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Zap, Send, Loader2, ChevronLeft, ChevronRight, X } from 'lucide-react';
import {
  format, addMonths, subMonths, startOfMonth, endOfMonth,
  eachDayOfInterval, getDay, isSameDay, isToday, isBefore, startOfDay
} from 'date-fns';
import { fr } from 'date-fns/locale';

const TIME_SLOTS = [];
for (let h = 7; h <= 19; h++) {
  TIME_SLOTS.push(`${String(h).padStart(2, '0')}:00`);
  TIME_SLOTS.push(`${String(h).padStart(2, '0')}:30`);
}

export const FlashPromosTab = ({ sites, activities, user, t, flashPromoTrigger }) => {
  const isFranchise = user?.role === 'franchise_admin';
  const [siteId, setSiteId] = useState(isFranchise ? user.site_id : (sites[0]?.id || ''));
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Promo creation state
  const [selectedDate, setSelectedDate] = useState(null);
  const [discount, setDiscount] = useState(30);
  const [activityMode, setActivityMode] = useState('all');
  const [selectedActivityIds, setSelectedActivityIds] = useState([]);
  const [selectedTimeSlots, setSelectedTimeSlots] = useState([]);
  const [timeMode, setTimeMode] = useState('all'); // all, morning, afternoon, custom
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const siteActivities = activities.filter(a => a.site_id === siteId);

  const loadPromos = useCallback(async () => {
    try {
      const res = await adminAPI.getFlashPromos(siteId);
      setPromos(res.data);
    } catch { setPromos([]); }
    setLoading(false);
  }, [siteId]);

  useEffect(() => { loadPromos(); }, [loadPromos]);

  // Handle calendar trigger from AdminCalendar
  useEffect(() => {
    if (flashPromoTrigger?.date) {
      const d = new Date(flashPromoTrigger.date + 'T00:00:00');
      setSelectedDate(d);
      setCurrentMonth(d);
      if (flashPromoTrigger.activity_id) {
        setActivityMode('select');
        setSelectedActivityIds([flashPromoTrigger.activity_id]);
      }
      if (flashPromoTrigger.time_slot) {
        setTimeMode('custom');
        setSelectedTimeSlots([flashPromoTrigger.time_slot]);
      }
    }
  }, [flashPromoTrigger]);

  const getPromosForDate = (dateStr) => promos.filter(p => p.date === dateStr && p.active);

  const handleDeactivate = async (promoId) => {
    try {
      await adminAPI.deactivateFlashPromo(promoId);
      setPromos(prev => prev.map(p => p.id === promoId ? { ...p, active: false } : p));
      toast.success('Promo désactivée');
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const getEffectiveSlots = () => {
    if (timeMode === 'all') return [];
    if (timeMode === 'morning') return TIME_SLOTS.filter(t => parseInt(t) < 13);
    if (timeMode === 'afternoon') return TIME_SLOTS.filter(t => parseInt(t) >= 13);
    return selectedTimeSlots;
  };

  const handleSend = async () => {
    if (!selectedDate) { toast.error('Sélectionnez une date'); return; }
    setSending(true);
    try {
      const period = timeMode === 'all' ? 'day' : timeMode === 'morning' ? 'morning' : timeMode === 'afternoon' ? 'afternoon' : 'custom';
      const payload = {
        site_id: siteId,
        date: format(selectedDate, 'yyyy-MM-dd'),
        discount_percent: discount,
        period,
        time_slots: timeMode === 'custom' ? selectedTimeSlots : undefined,
        activity_ids: activityMode === 'all' ? [] : selectedActivityIds,
        message: message || undefined,
        origin_url: window.location.origin,
      };
      const res = await adminAPI.sendPromoBatch(payload);
      toast.success(res.data.message);
      loadPromos();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    setSending(false);
  };

  const toggleTimeSlot = (slot) => {
    setSelectedTimeSlots(prev => prev.includes(slot) ? prev.filter(s => s !== slot) : [...prev, slot]);
  };

  const toggleActivity = (actId) => {
    setSelectedActivityIds(prev => prev.includes(actId) ? prev.filter(id => id !== actId) : [...prev, actId]);
  };

  // Calendar rendering
  const calDays = eachDayOfInterval({ start: startOfMonth(currentMonth), end: endOfMonth(currentMonth) });
  const firstDayOffset = (getDay(startOfMonth(currentMonth)) + 6) % 7;
  const selectedDateStr = selectedDate ? format(selectedDate, 'yyyy-MM-dd') : null;
  const dayPromos = selectedDateStr ? getPromosForDate(selectedDateStr) : [];

  return (
    <div className="space-y-6" data-testid="flash-promos-tab">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <Zap className="w-5 h-5 text-yellow-400" />
          <h3 className="font-heading text-lg font-bold text-white">Promos Flash</h3>
          {!isFranchise && (
            <Select value={siteId} onValueChange={v => { setSiteId(v); setLoading(true); }}>
              <SelectTrigger className="w-[160px] bg-[#1A1A1A] border-[#2A2A2A] text-white text-xs h-9" data-testid="promo-site-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-[#2A2A2A]">
                {sites.map(s => <SelectItem key={s.id} value={s.id}>{s.city}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* LEFT: Calendar */}
        <div className="lg:col-span-1">
          <div className="bg-[#1A1A1A] rounded-2xl border border-[#2A2A2A] p-4">
            {/* Month navigation */}
            <div className="flex items-center justify-between mb-4">
              <Button variant="ghost" size="sm" onClick={() => setCurrentMonth(subMonths(currentMonth, 1))} className="text-slate-400 hover:text-white" data-testid="promo-cal-prev">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm font-bold text-white capitalize">{format(currentMonth, 'MMMM yyyy', { locale: fr })}</span>
              <Button variant="ghost" size="sm" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))} className="text-slate-400 hover:text-white" data-testid="promo-cal-next">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>

            {/* Day headers */}
            <div className="grid grid-cols-7 gap-1 mb-1">
              {['Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa', 'Di'].map(d => (
                <div key={d} className="text-center text-[10px] font-semibold text-slate-600 py-1">{d}</div>
              ))}
            </div>

            {/* Days grid */}
            <div className="grid grid-cols-7 gap-1">
              {Array(firstDayOffset).fill(null).map((_, i) => <div key={`e-${i}`} />)}
              {calDays.map(d => {
                const dateStr = format(d, 'yyyy-MM-dd');
                const today = isToday(d);
                const isPast = isBefore(d, startOfDay(new Date()));
                const isSelected = selectedDate && isSameDay(d, selectedDate);
                const datePromos = getPromosForDate(dateStr);
                const hasPromos = datePromos.length > 0;

                return (
                  <button
                    key={d.toISOString()}
                    onClick={() => !isPast && setSelectedDate(d)}
                    disabled={isPast}
                    className={`p-1.5 rounded-lg text-center min-h-[38px] transition-all relative ${
                      isPast ? 'opacity-30 cursor-not-allowed' :
                      isSelected ? 'bg-yellow-500/20 border border-yellow-500 ring-1 ring-yellow-500/30' :
                      today ? 'bg-[#2A2A2A] border border-[#333]' :
                      'hover:bg-[#222] border border-transparent'
                    }`}
                    data-testid={`promo-cal-day-${dateStr}`}
                  >
                    <span className={`text-xs ${isSelected ? 'text-yellow-400 font-bold' : today ? 'text-[#00FF00] font-bold' : 'text-slate-400'}`}>
                      {format(d, 'd')}
                    </span>
                    {hasPromos && (
                      <div className="absolute bottom-0.5 left-1/2 -translate-x-1/2">
                        <div className="w-1.5 h-1.5 rounded-full bg-yellow-400" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Active promos for selected day */}
            {selectedDate && dayPromos.length > 0 && (
              <div className="mt-4 pt-3 border-t border-[#2A2A2A]">
                <h4 className="text-xs font-semibold text-yellow-400 mb-2">Promos actives ce jour</h4>
                <div className="space-y-1.5 max-h-48 overflow-y-auto">
                  {dayPromos.map(p => (
                    <div key={p.id} className="flex items-center justify-between bg-[#0A0A0A] rounded-lg p-2 border border-[#2A2A2A]" data-testid={`promo-item-${p.id}`}>
                      <div className="flex items-center gap-2 min-w-0">
                        <Zap className="w-3 h-3 text-yellow-400 flex-shrink-0" />
                        <span className="text-white text-xs truncate">{p.activity_name}</span>
                        <span className="text-slate-500 text-[10px] flex-shrink-0">{p.time_slot}</span>
                        <Badge className="bg-yellow-500/15 text-yellow-400 border-0 text-[10px] flex-shrink-0">-{p.discount_percent}%</Badge>
                      </div>
                      <button onClick={() => handleDeactivate(p.id)} className="text-red-400/60 hover:text-red-400 text-[10px] flex-shrink-0 ml-1" data-testid={`promo-deactivate-${p.id}`}>
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* RIGHT: Creation form */}
        <div className="lg:col-span-2">
          {!selectedDate ? (
            <div className="bg-[#1A1A1A] rounded-2xl border border-[#2A2A2A] p-8 flex flex-col items-center justify-center min-h-[400px]">
              <Zap className="w-12 h-12 text-yellow-400/20 mb-4" />
              <p className="text-slate-500 text-sm text-center">Sélectionnez une date dans le calendrier<br />pour créer une promo flash</p>
            </div>
          ) : (
            <div className="bg-[#1A1A1A] rounded-2xl border border-[#2A2A2A] p-6 space-y-5">
              <div className="flex items-center justify-between">
                <h3 className="font-heading text-base font-bold text-white capitalize flex items-center gap-2">
                  <Zap className="w-4 h-4 text-yellow-400" />
                  Promo du {format(selectedDate, 'EEEE d MMMM', { locale: fr })}
                </h3>
                <Button variant="ghost" size="sm" onClick={() => setSelectedDate(null)} className="text-slate-500 hover:text-white">
                  <X className="w-4 h-4" />
                </Button>
              </div>

              {/* Discount */}
              <div>
                <Label className="text-xs text-slate-400 mb-1.5 block">Réduction</Label>
                <div className="flex items-center gap-3">
                  {[10, 20, 30, 40, 50].map(v => (
                    <button key={v} onClick={() => setDiscount(v)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                        discount === v ? 'bg-yellow-500 text-black' : 'bg-[#0A0A0A] text-slate-400 border border-[#2A2A2A] hover:border-yellow-500/30'
                      }`} data-testid={`promo-discount-${v}`}>
                      -{v}%
                    </button>
                  ))}
                  <Input type="number" min={5} max={90} value={discount} onChange={e => setDiscount(parseInt(e.target.value) || 30)}
                    className="bg-[#0A0A0A] border-[#2A2A2A] text-white w-20 text-xs h-8" data-testid="promo-discount-custom" />
                </div>
              </div>

              {/* Activities */}
              <div>
                <Label className="text-xs text-slate-400 mb-1.5 block">Activités</Label>
                <div className="flex gap-2 mb-2">
                  <Button size="sm" variant={activityMode === 'all' ? 'default' : 'outline'}
                    onClick={() => { setActivityMode('all'); setSelectedActivityIds([]); }}
                    className={activityMode === 'all' ? 'bg-yellow-500 text-black hover:bg-yellow-400 text-xs' : 'border-[#2A2A2A] text-slate-400 text-xs'}
                    data-testid="promo-all-activities">
                    Toutes
                  </Button>
                  <Button size="sm" variant={activityMode === 'select' ? 'default' : 'outline'}
                    onClick={() => setActivityMode('select')}
                    className={activityMode === 'select' ? 'bg-yellow-500 text-black hover:bg-yellow-400 text-xs' : 'border-[#2A2A2A] text-slate-400 text-xs'}
                    data-testid="promo-select-activities">
                    Choisir
                  </Button>
                </div>
                {activityMode === 'select' && (
                  <div className="flex flex-wrap gap-2">
                    {siteActivities.map(a => (
                      <label key={a.id} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer transition-all ${
                        selectedActivityIds.includes(a.id) ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/40' : 'bg-[#0A0A0A] text-slate-400 border border-[#2A2A2A] hover:border-yellow-500/30'
                      }`} data-testid={`promo-activity-${a.id}`}>
                        <Checkbox checked={selectedActivityIds.includes(a.id)} onCheckedChange={() => toggleActivity(a.id)}
                          className="w-3.5 h-3.5 border-[#333] data-[state=checked]:bg-yellow-500 data-[state=checked]:border-yellow-500" />
                        {a.name}
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Time slots */}
              <div>
                <Label className="text-xs text-slate-400 mb-1.5 block">Horaires</Label>
                <div className="flex flex-wrap gap-2 mb-3">
                  {[
                    { key: 'all', label: 'Journée entière' },
                    { key: 'morning', label: 'Matin (7h-13h)' },
                    { key: 'afternoon', label: 'Après-midi (13h-20h)' },
                    { key: 'custom', label: 'Personnalisé' },
                  ].map(opt => (
                    <Button key={opt.key} size="sm"
                      variant={timeMode === opt.key ? 'default' : 'outline'}
                      onClick={() => { setTimeMode(opt.key); if (opt.key !== 'custom') setSelectedTimeSlots([]); }}
                      className={`text-xs ${timeMode === opt.key ? 'bg-yellow-500 text-black hover:bg-yellow-400' : 'border-[#2A2A2A] text-slate-400'}`}
                      data-testid={`promo-time-${opt.key}`}>
                      {opt.label}
                    </Button>
                  ))}
                </div>
                {timeMode === 'custom' && (
                  <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto p-2 bg-[#0A0A0A] rounded-xl border border-[#2A2A2A]">
                    {TIME_SLOTS.map(slot => (
                      <button key={slot} onClick={() => toggleTimeSlot(slot)}
                        className={`px-2 py-1 rounded text-[11px] font-mono font-medium transition-all ${
                          selectedTimeSlots.includes(slot)
                            ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/40'
                            : 'bg-[#111] text-slate-500 border border-[#222] hover:border-yellow-500/30 hover:text-slate-300'
                        }`} data-testid={`promo-slot-${slot}`}>
                        {slot}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Message */}
              <div>
                <Label className="text-xs text-slate-400 mb-1.5 block">Message (optionnel)</Label>
                <Textarea value={message} onChange={e => setMessage(e.target.value)} placeholder="Profitez de nos offres exceptionnelles !"
                  className="bg-[#0A0A0A] border-[#2A2A2A] text-white h-16 resize-none text-xs" data-testid="promo-batch-message" />
              </div>

              {/* Summary */}
              <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-3">
                <p className="text-xs text-yellow-400">
                  <strong>Récap :</strong> -{discount}% sur {activityMode === 'all' ? 'toutes les activités' : `${selectedActivityIds.length} activité(s)`} — {
                    timeMode === 'all' ? 'Journée entière' : timeMode === 'morning' ? 'Matin' : timeMode === 'afternoon' ? 'Après-midi' :
                    `${selectedTimeSlots.length} créneau(x)`
                  } le {format(selectedDate, 'd MMMM yyyy', { locale: fr })}
                </p>
              </div>

              {/* Send */}
              <Button onClick={handleSend}
                disabled={sending || (activityMode === 'select' && selectedActivityIds.length === 0) || (timeMode === 'custom' && selectedTimeSlots.length === 0)}
                className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 text-black font-bold hover:from-yellow-400 hover:to-orange-400 h-11" data-testid="promo-batch-send">
                {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                Envoyer la promo flash
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
